import { ContextWindow } from "@asup/context-menu";
import { ReactNode } from "react";
import { AioSelect } from "../additional/aioSelect";
import styles from "../aic.module.css";
import { useBlockContext } from "./BlockContentProvider";
import { UPDATE_LINE } from "./blockReducer";
import { AibLineType } from "./interface";
import { OriginalTextEditor } from "./OriginalTextEditor";

interface AibOptionsWindowProps {
  aifid: string;
  onClose: () => void;
  showWindow: boolean;
}

/**
 * Displays the options window for a line.
 *
 * @param props - Destructured parameter object
 * @param props.aifid - The block instance id (`aifid`) identifying which line's options to show.
 * @param props.onClose - Callback invoked when the options window is closed.
 * @param props.showWindow - Controls whether the options window is visible.
 */
export const AibOptionsWindow = ({
  aifid,
  onClose,
  showWindow,
}: AibOptionsWindowProps): ReactNode => {
  const { state, dispatch } = useBlockContext();
  const ix = state.lines.findIndex((l) => l.aifid === aifid);
  const thisLine = state.lines[ix];

  return !thisLine ? null : (
    <ContextWindow
      id={`${state.id}-${ix}-options-window`}
      title="Line options"
      visible={showWindow}
      onClose={onClose}
    >
      <div className={styles.aiwBodyRow}>
        <AioSelect
          id={`${state.id}-${ix}-linetype`}
          label="Line type"
          availableValues={["Left only", "Center only", "Left, Center and Right", "Left and Right"]}
          value={thisLine.lineType}
          setValue={
            !state.disabled && thisLine.canChangeType
              ? (ret) =>
                  dispatch({
                    operation: UPDATE_LINE,
                    line: { ...thisLine, lineType: ret as AibLineType },
                  })
              : undefined
          }
        />
      </div>
      {thisLine.lineType.toString().includes("Left") && (
        <OriginalTextEditor
          aifid={aifid}
          position="left"
        />
      )}
      {thisLine.lineType.toString().includes("Center") && (
        <OriginalTextEditor
          aifid={aifid}
          position="center"
        />
      )}{" "}
      {thisLine.lineType.toString().includes("Right") && (
        <OriginalTextEditor
          aifid={aifid}
          position="right"
        />
      )}
    </ContextWindow>
  );
};

AibOptionsWindow.displayName = "AibOptionsWindow";
